/**
 * stateEffects.js — State Effect Executors (5 types)
 *
 * Manages session state and character flags.
 * State effects: session state is ephemeral (React state),
 * character flags persist (Firebase).
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #175)
 */

import { resolveExpression } from '../expressions';

// ─── State Effect Executors ─────────────────────────────────────────────────

export const STATE_EFFECT_EXECUTORS = {
  /**
   * Set a key on session state.
   * effect: { type: 'set_session_state', key, value }
   */
  set_session_state: (effect, context) => {
    const resolved = resolveExpression(effect.value, context);
    return {
      characterMutated: false,
      sessionStateUpdates: { [effect.key]: resolved },
    };
  },

  /**
   * Clear a key from session state (set to null).
   * effect: { type: 'clear_session_state', key }
   */
  clear_session_state: (effect) => {
    return {
      characterMutated: false,
      sessionStateUpdates: { [effect.key]: null },
    };
  },

  /**
   * Set a flag on the character object.
   * effect: { type: 'set_character_flag', flag, value? }
   * If value is omitted, defaults to true.
   */
  set_character_flag: (effect, context) => {
    const value = effect.value !== undefined
      ? resolveExpression(effect.value, context)
      : true;
    context.character[effect.flag] = value;
    return { characterMutated: true };
  },

  /**
   * Clear (delete) a flag from the character object.
   * effect: { type: 'clear_character_flag', flag }
   */
  clear_character_flag: (effect, context) => {
    delete context.character[effect.flag];
    return { characterMutated: true };
  },

  /**
   * Toggle a boolean flag on the character object.
   * effect: { type: 'toggle_character_flag', flag }
   */
  toggle_character_flag: (effect, context) => {
    context.character[effect.flag] = !context.character[effect.flag];
    return { characterMutated: true };
  },
};
